import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { Badge } from './ui/badge';
import { MapPin, Clock, DollarSign, Globe, Zap, ChevronDown, ChevronUp, Bus, Utensils, Star } from 'lucide-react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

import { API } from '../config';

function TransportRow({ item }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-border last:border-0">
      <div className="w-8 h-8 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Bus className="w-4 h-4 text-[#1E3A8A]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold">{item.name}</span>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge className="text-[10px] bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20">{item.time}</Badge>
            <span className="text-xs text-muted-foreground font-medium">{item.cost}</span>
          </div>
        </div>
        {item.notes && <p className="text-xs text-muted-foreground mt-0.5">{item.notes}</p>}
      </div>
    </div>
  );
}

export default function CityGuide({ destinationCode }) {
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!destinationCode || destinationCode === 'UNK') {
      setLoading(false);
      return;
    }
    axios.get(`${API}/cities/${destinationCode}`)
      .then(r => { setGuide(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [destinationCode]);

  if (loading) {
    return (
      <div className="mt-8 space-y-3">
        <Skeleton className="h-6 w-48 rounded" />
        <Skeleton className="h-48 rounded-2xl" />
      </div>
    );
  }

  if (!guide) return null;

  const hasRichContent = guide.top_attractions?.length > 0 || guide.food_scene;

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="mt-8"
      data-testid="city-guide-section"
    >
      {/* Section header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <MapPin className="w-4 h-4 text-[#FF4500]" />
            About {guide.city}
            {guide.flag && <span className="text-base">{guide.flag}</span>}
          </h2>
          <p className="text-xs text-muted-foreground">{guide.country}</p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground items-center">
          {guide.currency && (
            <span className="flex items-center gap-1">
              <DollarSign className="w-3 h-3" /> {guide.currency}
            </span>
          )}
          {guide.best_time && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" /> Best: {guide.best_time}
            </span>
          )}
          {guide.language && (
            <span className="flex items-center gap-1 hidden sm:flex">
              <Globe className="w-3 h-3" /> {guide.language.split('/')[0]}
            </span>
          )}
        </div>
      </div>

      {/* Hero card */}
      <Card className="fly-card overflow-hidden">
        {/* Hero image */}
        {guide.hero_image && (
          <div className="relative h-36 sm:h-48 overflow-hidden">
            <img
              src={guide.hero_image}
              alt={guide.city}
              className="w-full h-full object-cover"
              onError={e => { e.target.style.display = 'none'; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-3 left-4 text-white">
              <p className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{guide.city}</p>
              <p className="text-xs text-white/80">{guide.country}</p>
            </div>
          </div>
        )}

        <div className="p-4 space-y-4">
          {/* Overview */}
          {guide.overview && (
            <p className="text-sm text-muted-foreground leading-relaxed">{guide.overview}</p>
          )}

          {/* Airport to city transport */}
          {guide.airport_to_city?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                <Bus className="w-3.5 h-3.5 text-[#FF4500]" /> Airport to City
              </h3>
              <div className="divide-y divide-border">
                {guide.airport_to_city.map((item, i) => (
                  <TransportRow key={i} item={item} />
                ))}
              </div>
            </div>
          )}

          {/* Expandable content */}
          {hasRichContent && (
            <>
              <AnimatePresence>
                {expanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-4 overflow-hidden"
                  >
                    {/* Top attractions */}
                    {guide.top_attractions?.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                          <Star className="w-3.5 h-3.5 text-[#FF4500]" /> Top Attractions
                        </h3>
                        <div className="grid sm:grid-cols-2 gap-1.5">
                          {guide.top_attractions.map((a, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm py-1">
                              <span className="w-5 h-5 rounded-full bg-[#FF4500]/10 text-[#FF4500] text-[10px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                              <span className="text-muted-foreground">{a}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Food scene */}
                    {guide.food_scene && (
                      <div>
                        <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                          <Utensils className="w-3.5 h-3.5 text-[#FF4500]" /> Food & Dining
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{guide.food_scene}</p>
                      </div>
                    )}

                    {/* Local tips */}
                    {guide.local_tips?.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                          <Zap className="w-3.5 h-3.5 text-[#FF4500]" /> Traveler Tips
                        </h3>
                        <ul className="space-y-1.5">
                          {guide.local_tips.map((tip, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <span className="text-[#10B981] mt-0.5 flex-shrink-0">✓</span>
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                onClick={() => setExpanded(e => !e)}
                className="flex items-center gap-1.5 text-xs text-[#FF4500] hover:text-[#E63F00] font-medium transition-colors"
                data-testid="city-guide-expand-btn"
              >
                {expanded ? (
                  <><ChevronUp className="w-3.5 h-3.5" /> Show less</>
                ) : (
                  <><ChevronDown className="w-3.5 h-3.5" /> See attractions, food & tips</>
                )}
              </button>
            </>
          )}
        </div>
      </Card>
    </motion.section>
  );
}
